import { memo, useCallback, useMemo } from 'react'
import { usePositionedMouseListeners } from '../../../../hooks'
import { logAmplitudeEvent } from '../../../../utils'
import { MentionIcon } from '../../../icons'
import { useCloseMentions, useIsShowingMentions, useOpenMentions } from '../mentions/atoms'
import { useIsDictating, useIsVoiceModeActive } from '../../../../store/atoms'
import { useActiveAudioNoteId, useHasChatInput, useIsTurnLive } from '../../../../store'
import { useSubmitComposer } from '../hooks/use-submit-composer'
import { useVoiceModeToggle } from '../hooks/use-voice-mode-toggle'
import { FeatureHighlight } from '../../../elements/FeatureHighlight'
import { useStopMessage } from '../../../../hooks/useCancelMessage'
import { AudioActivity } from '../../../elements/AudioActivity'
import { LoadingSpinner } from '../../../elements/LoadingSpinner'
import { Button, Tooltip } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { Mic } from 'lucide-react'
import { UsageIndicator } from './usage-indicator'
import { ReasoningEffortMenu } from './reasoning-effort-menu'

/**
 * Shared hover/press motion for the three input controls (mic, stop, mentions),
 * so they read as one set.
 *
 * `Button`'s base transition leaves `border-color` out of the property list —
 * the `secondary` variant's `` snapped — and runs
 * `transform` on the same 150ms ease-in-out as the colors, so a click shorter
 * than 150ms reversed mid-flight and never registered as a press. Colors ease
 * in over 150ms; the press scale drops in 80ms and eases back out over 100ms,
 * which stays smooth whichever state it's interrupted from.
 */
// Spelled out rather than interpolated: Tailwind's scanner only sees whole
// class strings in the source.
const CONTROL_MOTION_CLASS = [
  '[transition:background-color_150ms_ease-out,border-color_150ms_ease-out,color_150ms_ease-out,outline-color_150ms_ease-out,box-shadow_150ms_ease-out,transform_100ms_cubic-bezier(0.22,1,0.36,1)]',
  'active:[transition:background-color_150ms_ease-out,border-color_150ms_ease-out,color_150ms_ease-out,outline-color_150ms_ease-out,box-shadow_150ms_ease-out,transform_80ms_cubic-bezier(0.4,0,0.2,1)]',
  // Colors still fade — it's the scale that's motion.
  'motion-reduce:active:scale-100',
].join(' ')

export const ChatInputButtons = memo(
  ({
    chatId,
    handleSubmit,
    isLoading,
    onSubmit,
  }: {
    chatId?: string
    handleSubmit?: () => void
    isLoading?: boolean
    onSubmit?: (conversationId: string) => void
  }) => {
    return (
      <div className={'ml-auto flex min-w-[70px] items-center justify-end gap-2 overflow-hidden'}>
        <ReasoningEffortMenu chatId={chatId} />
        <UsageIndicator chatId={chatId} />
        <VoiceModeButton />
        <StopButton chatId={chatId} />
        <LoadingButton isLoading={isLoading} />
        <SubmitButton chatId={chatId} handleSubmit={handleSubmit} isLoading={isLoading} onSubmit={onSubmit} />
        <MentionsButton chatId={chatId} isLoading={isLoading} />
      </div>
    )
  },
)
ChatInputButtons.displayName = 'ChatInputButtons'

const MentionsButton = memo(({ chatId, isLoading }: { chatId?: string; isLoading?: boolean }) => {
  const hasChatInput = useHasChatInput()
  const isShowingMentions = useIsShowingMentions()
  const openMentions = useOpenMentions()
  const closeMentions = useCloseMentions()
  const isVoiceModeActive = useIsVoiceModeActive()
  const isCurrentChatStreaming = useIsTurnLive(chatId)
  const isDictating = useIsDictating()

  const onClickMentions = useCallback(() => {
    // Both directions go through the menu's own actions, not a bare boolean:
    // it also carries a slash-mode flag, a query and a highlight index that
    // belong to the session being opened or ended.
    if (isShowingMentions) {
      closeMentions()
    } else {
      openMentions({ searchField: true })
    }
    logAmplitudeEvent('Click Chat Input Button', { name: 'Mentions' })
  }, [isShowingMentions, closeMentions, openMentions])

  const mentionsMouseEvents = usePositionedMouseListeners(onClickMentions)

  const shouldShow = useMemo(() => {
    return !(isVoiceModeActive || isCurrentChatStreaming || hasChatInput || isDictating || isLoading)
  }, [isVoiceModeActive, isCurrentChatStreaming, hasChatInput, isDictating, isLoading])

  if (!shouldShow) return null

  return (
    <FeatureHighlight
      id={'chat-mentions'}
      previousId={'chat-updates-attachments'}
      hint={'4 of 4'}
      position={'top'}
      title={'Mention Models & More'}
      description={'Use models, shortcuts, or tools in your messages. You can type @ to quickly pick one.'}
      offset={8}
      backgroundOverlay={false}
      primaryAction={{
        label: 'Continue',
        dismiss: true,
      }}
      disabled={true}
    >
      <Tooltip>
        <Tooltip.Trigger
          delay={200}
          render={
            <Button
              data-tour-section="chat-mentions"
              size="lg"
              variant="outline"
              shape="circle"
              className={twMerge(
                '[&_svg]:!size-4.5',
                CONTROL_MOTION_CLASS,
                // Open reads like any open Menu.Trigger: the pressed fill, held through hover.
                isShowingMentions ? 'bg-element-active hover:bg-element-active text-foreground' : '',
              )}
              onClick={(e) => e.stopPropagation()}
              {...mentionsMouseEvents}
            />
          }
        >
          <svg width="18" height="18" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <MentionIcon size={17} />
          </svg>
        </Tooltip.Trigger>
        <Tooltip.Content>Models, Shortcuts & Tools</Tooltip.Content>
      </Tooltip>
    </FeatureHighlight>
  )
})
MentionsButton.displayName = 'MentionsButton'

const SubmitButton = memo(
  ({
    chatId,
    handleSubmit,
    isLoading,
    onSubmit,
  }: {
    chatId?: string
    handleSubmit?: () => void
    isLoading?: boolean
    onSubmit?: (conversationId: string) => void
  }) => {
    const hasChatInput = useHasChatInput()
    const isDictating = useIsDictating()
    const activeAudioNoteId = useActiveAudioNoteId()
    const { isQueueFull, isBlockedForButton, dispatchSubmit } = useSubmitComposer({ chatId, onSubmit })

    const shouldShow = useMemo(() => {
      if (isLoading) return false
      return hasChatInput || isDictating
    }, [hasChatInput, isDictating, isLoading])

    if (!shouldShow) return null

    return (
      <Tooltip>
        <Tooltip.Trigger render={<div className="relative flex" />}>
          <Button
            data-tour-section="chat-submit"
            size="lg"
            variant="outline"
            shape="circle"
            className={twMerge(
              '[&_svg]:!size-4',
              hasChatInput &&
                'bg-info text-on-info hover:bg-info-hover hover:text-on-info border-transparent hover:border-transparent',
              isBlockedForButton && 'cursor-not-allowed opacity-50',
            )}
            onClick={() => {
              if (!isBlockedForButton) {
                handleSubmit ? handleSubmit() : dispatchSubmit()
                logAmplitudeEvent('Click Chat Input Button', { name: 'Submit' })
              }
            }}
            disabled={isBlockedForButton}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.27734 12.4346C5.96289 12.4346 5.70996 12.3343 5.51855 12.1338C5.32715 11.9333 5.23145 11.6689 5.23145 11.3408V4.56641L5.34082 1.98926L5.92871 2.41309L4.18555 4.4707L2.73633 5.91309C2.64062 6.01335 2.53125 6.09538 2.4082 6.15918C2.28971 6.21842 2.15072 6.24805 1.99121 6.24805C1.70866 6.24805 1.47168 6.1569 1.28027 5.97461C1.09342 5.78776 1 5.54395 1 5.24316C1 4.96517 1.1071 4.7168 1.32129 4.49805L5.50488 0.307617C5.60059 0.211914 5.7168 0.136719 5.85352 0.0820312C5.99479 0.0273438 6.13607 0 6.27734 0C6.41862 0 6.55762 0.0273438 6.69434 0.0820312C6.83561 0.136719 6.95182 0.211914 7.04297 0.307617L11.2266 4.49805C11.4453 4.7168 11.5547 4.96517 11.5547 5.24316C11.5547 5.54395 11.459 5.78776 11.2676 5.97461C11.0807 6.1569 10.8483 6.24805 10.5703 6.24805C10.4108 6.24805 10.2695 6.21842 10.1465 6.15918C10.0234 6.09538 9.91406 6.01335 9.81836 5.91309L8.36914 4.4707L6.61914 2.41309L7.20703 1.98926L7.32324 4.56641V11.3408C7.32324 11.6689 7.22526 11.9333 7.0293 12.1338C6.83789 12.3343 6.58724 12.4346 6.27734 12.4346Z"
                fill="currentColor"
              />
            </svg>
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content>
          {isQueueFull
            ? 'Queue full — cancel one to add another'
            : activeAudioNoteId
              ? 'Ask about audio note'
              : 'Submit'}
          <Tooltip.Hotkey hotkey="↩" />
        </Tooltip.Content>
      </Tooltip>
    )
  },
)
SubmitButton.displayName = 'SubmitButton'

const LoadingButton = memo(({ isLoading }: { isLoading?: boolean }) => {
  if (!isLoading) return null

  return (
    <Button
      size="lg"
      variant="outline"
      shape="circle"
      className="bg-info text-on-info hover:bg-info-hover hover:text-on-info cursor-default border-transparent hover:border-transparent"
      disabled
    >
      <LoadingSpinner size="13px" className="text-white" />
    </Button>
  )
})
LoadingButton.displayName = 'LoadingButton'

const VoiceModeButton = memo(() => {
  const { isVoiceModeActive, isDictating, isSettingUpAudio, isAwaitingSegment, isDownloadingModel, onToggle } =
    useVoiceModeToggle()

  const isLoading = isAwaitingSegment || isSettingUpAudio || isDownloadingModel
  const isActive = isVoiceModeActive || isDictating

  const icon = isLoading ? (
    <LoadingSpinner size="18px" className="text-green" />
  ) : isActive ? (
    <AudioActivity />
  ) : (
    <Mic />
  )

  // As with Mentions, the hover colour only holds the live state — it never
  // introduces green on an idle button.
  const greenClassName =
    isActive || isLoading ? 'text-success-subtle hover:text-success-subtle bg-success/15 hover:bg-success/20' : ''

  return (
    <Button
      onClick={onToggle}
      size="lg"
      variant="outline"
      shape="circle"
      className={twMerge('[&_svg]:!size-4.5', CONTROL_MOTION_CLASS, greenClassName)}
    >
      {icon}
    </Button>
  )
})
VoiceModeButton.displayName = 'VoiceModeButton'

const StopButton = memo(({ chatId }: { chatId?: string }) => {
  const stopMessage = useStopMessage()
  const isCurrentChatStreaming = useIsTurnLive(chatId)

  const onClickStop = useCallback(() => {
    if (chatId) {
      stopMessage(chatId)
    }
  }, [stopMessage, chatId])

  // Scoped to the chat this input submits to, not the app-wide active chat:
  // the sidepanel passes no `chatId` outside a Chat view, so keying off the
  // global id put Stop on the Home input whenever any chat streamed in the
  // background — next to Mentions, which gates on the prop and so stayed
  // visible too, showing both halves of what is meant to be one slot.
  const shouldShow = !!(chatId && isCurrentChatStreaming)

  if (!shouldShow) return null

  return (
    <Button
      size="lg"
      variant="outline"
      shape="circle"
      className={twMerge('[&_svg]:!size-3', CONTROL_MOTION_CLASS)}
      onClick={() => {
        onClickStop()
        logAmplitudeEvent('Click Chat Input Button', { name: 'Stop' })
      }}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="12" height="12" rx="2" fill="currentColor" />
      </svg>
    </Button>
  )
})
StopButton.displayName = 'StopButton'
